import { FunctionComponent, useMemo } from 'react';
import styles from './styles.module.scss';
import { useCoursePaymentOptions } from '../../../../hooks/useCoursePaymentOptions';
import { KlarnaIcon, PaypalCCIcon } from '../../../Icons';
import { PaymentMethod } from '@shared/types';
import { Label } from './Label';
import Card from '../../../Card';

export const SPREADABLE_PAYMENT_METHODS: PaymentMethod[] = ['klarna', 'paypal'];

export type SpreadTheCostProps = {
    educatorSlug: string;
    courseSlug: string;
    className?: string;
    isFree?: boolean;
};

export const SpreadTheCost: FunctionComponent<SpreadTheCostProps> = ({
    className,
    educatorSlug,
    courseSlug,
    isFree = false,
}) => {
    const { data: paymentOptions } = useCoursePaymentOptions({ educatorSlug, courseSlug });

    const enabledPaymentOptions = useMemo(() => {
        if (!paymentOptions) {
            return [];
        }

        return Object.keys(paymentOptions).reduce((optionNames: PaymentMethod[], optionKey) => {
            if (!SPREADABLE_PAYMENT_METHODS.includes(optionKey as PaymentMethod)) {
                return optionNames;
            }

            if (paymentOptions[optionKey as PaymentMethod].enabled === false) {
                return optionNames;
            }

            return optionNames.concat([optionKey as PaymentMethod]);
        }, []);
    }, [paymentOptions]);

    if (isFree) {
        return null;
    }

    if (enabledPaymentOptions.length === 0) {
        return null;
    }

    return (
        <Card className={className}>
            <div className={styles.container}>
                <div className={styles.icons}>
                    <Icons paymentOptions={enabledPaymentOptions} />
                </div>
                <Label enabledPaymentOptions={enabledPaymentOptions} />
            </div>
        </Card>
    );
};

type IconsProps = {
    paymentOptions: PaymentMethod[];
};

const Icons: FunctionComponent<IconsProps> = ({ paymentOptions }) => {
    return paymentOptions.map((option, index) => {
        switch (option) {
            case 'klarna': {
                return <KlarnaIcon key={index} height={25} width={65} />;
            }

            case 'paypal': {
                return <PaypalCCIcon key={index} height={25} width={65} />;
            }

            default: {
                return null;
            }
        }
    });
};
